import _ from 'lodash'
import Utils from '@/utils/utils'

import { InventoryStockTypes, UOMTypes, UOMTypeUnitsLookup, OrderItemEdgeworkTypes } from '@/store/types'

export default {
  methods: {
    // Finds the root item given a parent id
    findRootOrderItemForParentId(itemList, parentOrderItemId) {
      if(parentOrderItemId == null)
        return null

      let parentItem = itemList.find(item => item.id == parentOrderItemId)
      if(parentItem == null)
        return null
      
      let rootItem = this.findRootOrderItemForParentId(itemList, parentItem.parentOrderItemId)

      return rootItem || parentItem
    },

    // Finds the root item of an item
    // NOTE: Assumes this mixin is associated with a component containing 'itemList' listing of order items
    findItemRootForItem(item) {
      return this.findItemRootForItemInList(item, this.itemList)
    },

    // Finds the root item of an item in a given list
    findItemRootForItemInList(item, itemList) {
      if(item.parentOrderItemId == null)
        return item
        
      var rootItemId = item.parentOrderItemId
      var nextParentId = rootItemId
      var foundRoot = false
      while(!foundRoot) {
        let parent = itemList.find(parentItem => parentItem.id == nextParentId)
        if(!parent)
          break
        rootItemId = parent.id
        nextParentId = parent.parentOrderItemId
      }
      return itemList.find(parentItem => parentItem.id == rootItemId)
    },

    // Returns a flat list of all items in a group given any item in the group
    getItemGroupListForItem(itemList, itemId) {
      var item = itemList.find(item => item.id == itemId)
      let rootItem = this.findRootOrderItemForParentId(itemList, itemId) || item
      if(rootItem == null)
        return []

      var groupList = [rootItem]

      return this._buildGroupList(groupList, itemList, rootItem.id)
    },
    _buildGroupList(outputList, itemList, parentOrderItemId) {
      itemList.forEach(item => {
        if(item.parentOrderItemId == parentOrderItemId) {
          outputList.push(item)

          this._buildGroupList(outputList, itemList, item.id)
        }
      })
      return outputList
    },

    /**
     * Finds the 'primary' order item in a group of order items to apply minimum charge
     * Uses the first glass or root general order item (if no glass) in the group
     */
    findOrderItemGroupPrimaryitem(groupItemList) {
      var primaryItem = null

      // If only 1 item, use that
      if(groupItemList.length == 1)
        return groupItemList[0]

      // Find first glass item
      groupItemList.forEach(item => {
        if(item.stockType == InventoryStockTypes.glass && !primaryItem)
          primaryItem = item
      })

      // Just default to first item if glass one not found
      return primaryItem || groupItemList[0]
    },


    /**
     * Get the list of order items in the list that are editable
     * Order items that have been cancelled, invoiced or despatched or not editable
     */
    filterEditableOrderItemList(itemList) {
      return itemList.filter(orderItem => {
        let rootItem = this.findItemRootForItemInList(orderItem, itemList)

        if(rootItem.isCancelled || rootItem.isInvoiced)
          return false
        if(rootItem.invoicedQuantity > 0 || rootItem.despatchedQuantity > 0)
          return false

        return true
      })
    },


    /**
     * Build the order item list from a server into a flattened order item list for the UI
     * Adds default settings for the OrderItem list editor component
     * Shared by Orders / PurchaseOrders / Invoices
     */
    buildOrderItemList(itemList, parentItemId = null, outputList = [], level = 0, parentItemKey = 'parentOrderItemId') {
      let parentItem = itemList.find(orderItem => orderItem.id == parentItemId)

      // Flatten & order so children are after parents
      itemList.filter(orderItem => orderItem[parentItemKey] == parentItemId).forEach(orderItem => {
        var item = _.cloneDeep(orderItem)
        let rootItem = this.findRootOrderItemForParentId(itemList, item[parentItemKey])

        item.uomType = orderItem.stock.uomType
        item.uomDisplay = Utils.getLookupName(UOMTypeUnitsLookup, orderItem.stock.uomType)
        item.isParentItem = item[parentItemKey] == null
        item.level = level
        item.groupStockType = rootItem ? rootItem.stockType : item.stockType
        item.showingGlass = false
        item.unitPriceOverridden = !!orderItem.unitPriceOverridden
        item.isUnitsLocked = !orderItem.unitsOverridden
        item.isGlassSizeLocked = parentItem ? true : false
        item.isLinePriceLocked = true
        item.isNewItem = false

        if(item.isDeliveryItem)
          item.isFixed = true

        // If parent is a template & this glass is different size, default to unlock the glass size
        if(parentItem && parentItem.stockType == InventoryStockTypes.template && item.stockType == InventoryStockTypes.glass) {
          if(parentItem.glassWidth != item.glassWidth || parentItem.glassHeight != item.glassHeight) {
            item.isGlassSizeLocked = false
          }
        }

        // Setup stock details for editing
        // Build list of price groups that are active for the stock
        if(orderItem.stock.stockSalesPrices) {
          item.stock.availablePriceGroups = orderItem.stock.stockSalesPrices
          .filter(salesPrice => salesPrice.isActive)
          .map(salesPrice => salesPrice.stockPriceGroupId)
        }

        outputList.push(item)

        this.buildOrderItemList(itemList, item.id, outputList, level + 1, parentItemKey)
      })

      return outputList      
    },


    /**
     * Prepare the flat order item list into a nested structure for sending to the server
     * Add additional fields for storing based on local settings
     * Contains additional options to clean the output item list to reduce data sent to the server
     */
    prepareOrderItemListForUpdate(itemList, options = {}, parentOrderItemId = null) {
      return itemList.filter(item => item.parentOrderItemId == parentOrderItemId).map(item => {

        var output = _.clone(item)
        // Extra item details for storing
        output.unitsOverridden = !item.isUnitsLocked

        // Cleanup data to reduce request size
        if(options.cleanupStructures) {
          output.stock = {}
          output.stockPriceGroup = {}
          output.servicePriceRange = {}
          output.glassLevel = {}
          output.glassSize = {}
          output.taxRate = {}
        }
        if(options.cleanupEditor) {
          output.svgDisplayData = ''
          output.svgLabelData = ''
          output.svgLabelCornerData = ''
          output.editorData = {}
        }
        if(options.cleanupShapeData) {
          output.shapeData = {}
        }
        
        output.childItems = this.prepareOrderItemListForUpdate(itemList, options, item.id)
        return output
      })
    },


    /**
     * Generate the egdework details list for glass order items that aren't created by the order editor
     * Mutates the OrderItemList in-place with the 'edgeworkDetailsList' field for glass order items based on child service items and edgework configuration data
     * 
     * @param {Array<Object>} orderItemList - Array of objects containing the order item list
     * @param {Array<Object>} edgeworkConfigList - Array of objects containing OrderEditorEdgeworkConfig records from server
     *    Requires edgework stocks included with edgework config records
     */
    generateOrderItemStandardEdgeworkDetails(orderItemList, edgeworkConfigList) {
      let glassItems = orderItemList.filter(orderItem => {
        if(orderItem.stockType != InventoryStockTypes.glass)
          return false
        if(orderItem.isCancelled)
          return false
        return !orderItem.isEditorGeneratedItem
      })

      if(glassItems.length == 0)
        return

      glassItems.forEach(glassItem => {
        let serviceItems = orderItemList.filter(serviceItem => {
          return serviceItem.parentOrderItemId == glassItem.id && serviceItem.stockType == InventoryStockTypes.glassService
        })
        let edgeworkItems = serviceItems.filter(serviceItem => serviceItem.uomType == UOMTypes.length)

        glassItem.edgeworkDetailsList = []

        // Find first edgework item in order editor config that contains this stock item
        // There are shape type filters, but we ignore this for simple shapes
        edgeworkItems.forEach(edgeworkOrderItem => {
          if(!_.isArray(edgeworkOrderItem.edgeworkFlagList) || edgeworkOrderItem.edgeworkFlagList.length == 0)
            return

          let edgeworkConfig = edgeworkConfigList.find(edgeworkConfig => {
            if(!edgeworkConfig.orderEditorEdgeworkStocks)
              return false
            return edgeworkConfig.orderEditorEdgeworkStocks.find(stock => stock.stockId == edgeworkOrderItem.stockId) != null
          })

          // TODO: Stock override

          if(!edgeworkConfig)
            return

          let edgeAllowance = 0
          let edgeFeedHeight = 0

          // Required feed height for edgework
          // Based on height of the adjacent side to the edgework side
          // Gets largest height found
          edgeworkOrderItem.edgeworkFlagList.forEach(edgeFlag => {
            if(edgeFlag == OrderItemEdgeworkTypes.longA || edgeFlag == OrderItemEdgeworkTypes.longB)
              edgeFeedHeight = Math.max(edgeFeedHeight, Math.min(glassItem.glassWidth, glassItem.glassHeight))

            if(edgeFlag == OrderItemEdgeworkTypes.shortA || edgeFlag == OrderItemEdgeworkTypes.shortB)
              edgeFeedHeight = Math.max(edgeFeedHeight, Math.max(glassItem.glassWidth, glassItem.glassHeight))
          })


          // Find matching edgework allowance record
          if(_.isArray(edgeworkConfig.orderEditorEdgeworkAllowances)) {
            let edgeAllowanceInfo = edgeworkConfig.orderEditorEdgeworkAllowances.find(allowance => {
              if(allowance.servicePriceRangeId != glassItem.servicePriceRangeId)
                return null

              let stockPriceGroup = allowance.servicePriceRange.stockPriceGroups.find(stockPriceGroup => stockPriceGroup.id == glassItem.stockPriceGroupId)
              return stockPriceGroup != null
            })

            if(edgeAllowanceInfo) {
              edgeAllowance = edgeAllowanceInfo.allowanceAmount
            }
          }

          edgeworkOrderItem.edgeworkFlagList.forEach(edgeworkFlagType => {

            glassItem.edgeworkDetailsList.push({
              type: edgeworkFlagType,
              name: edgeworkOrderItem.description || edgeworkConfig.name,
              colour: edgeworkConfig.highlightColour,
              allowance: edgeAllowance,
              feedHeight: edgeFeedHeight,
              stockId: edgeworkOrderItem.stockId
            })
          })
        })
      })
    }
  }
}